import {Group, Paper, Text, Title} from '@mantine/core';
import {useCallback, useMemo, useState} from 'react';
import {FieldErrors, SubmitHandler} from 'react-hook-form';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {Error} from '../../api/generated/index.defs';
import {JobsService} from '../../api/generated/JobsService';
import {AsyncStateContainer} from '../../components/async-state-container';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {ROUTES} from '../../routes';
import {useJobStore} from '../job-store';
import classes from './job-archive.module.css';
import {jobPartKitLotNumberSchema, JobPartKitLotNumberSchema} from './schemas';
import {JobTypes} from "../../api/generated/enums.ts";

const Form = createForm<JobPartKitLotNumberSchema>();

export const JobPartKitLotNumber: React.FC = () => {
  const notifications = useNotification();
  const [errors, setErrors] = useState<FieldErrors<JobPartKitLotNumberSchema>>(
    {},
  );
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  const {job, isLoading, isJobReadonly, fetchJob, fetchReplacedParts} =
    useJobStore((state) => ({
      job: state.job,
      isLoading: state.isJobLoading,
      isJobReadonly: state.isJobReadonly,
      fetchJob: state.fetchJob,
      fetchReplacedParts: state.fetchReplacedParts,
    }));

  const defaultValues = useMemo(
    () => ({partKitLotNumber: job?.partKitLotNumber}),
    [job?.partKitLotNumber],
  );

  const setFormErrors = useCallback((serverErrors: Error[]) => {
    const fieldErrors: FieldErrors<JobPartKitLotNumberSchema> = {};
    serverErrors.forEach((error) => {
      if (error.propertyName === 'PartKitLotNumber') {
        fieldErrors.partKitLotNumber = {
          message: error.errorMessage,
          type: 'validate',
        };
      }
    });
    setErrors(fieldErrors);
  }, []);

  const onSubmit = useCallback<SubmitHandler<JobPartKitLotNumberSchema>>(
    async (value) => {
      const response = await JobsService.updatePartKitLotNumber({
        jobId: id,
        body: value,
      });
      await fetchJob(id);
      await fetchReplacedParts(id);
      if (response.hasErrors) {
        setFormErrors(response.errors ?? []);
        return response;
      }
      setFormErrors([]);
      notifications.success('Replaced parts updated');
    },
    [fetchJob, fetchReplacedParts, id, notifications, setFormErrors],
  );

  if (!isLoading && job?.jobType === JobTypes["Non-M&M"]) return null;

  return (
    <div>
      <Title fz='sm' className={classes.title}>
        Part Kit Lot Number
      </Title>
      <Paper withBorder p='10' shadow='sm' radius='0 0 6px 6px'>
        <AsyncStateContainer
          loading={isLoading && !defaultValues.partKitLotNumber}
        >
          <Form
            onSubmit={onSubmit}
            errors={errors}
            schema={jobPartKitLotNumberSchema}
            defaultValues={defaultValues}
          >
            {({formState: {isSubmitting}}) => (
              <>
                <Group align='flex-start'>
                  <Text size='sm'>
                    Enter Part Kit Lot Number or Enter Individual Lots Below
                  </Text>
                  <Form.TextInput
                    w={300}
                    errorProps={errorProps}
                    name='partKitLotNumber'
                    disabled={isJobReadonly}
                  />
                  <Form.Submit loading={isSubmitting} disabled={isJobReadonly}>
                    Process Part Kit Lot Number
                  </Form.Submit>
                </Group>
              </>
            )}
          </Form>
        </AsyncStateContainer>
      </Paper>
    </div>
  );
};

const errorProps = {
  w: 300,
};